import { useState } from "react";
import { Link } from "@tanstack/react-router";
import type { AddDomainResponse } from "@rua-watch/shared";
import {
  useDomains,
  useDiscoveredDomains,
  useDeleteDomain,
  useAddDomain,
  useVerifyDomain,
} from "../api/hooks";
import { fromUnix } from "../lib/format";

export function DomainListRoute() {
  const { data, isLoading, error } = useDomains();
  const discovered = useDiscoveredDomains();

  return (
    <div className="space-y-8">
      <div className="space-y-4">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">Monitored domains</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Only domains you've claimed appear here. Reports for other domains that reach your mailbox are
            held below until you claim them.
          </p>
        </div>
        {isLoading ? <p className="text-slate-500 dark:text-slate-400">Loading…</p> : null}
        {error ? <p className="text-red-700 dark:text-red-300">Failed to load.</p> : null}

        {data ? (
          data.length === 0 ? (
            <p className="rounded-lg border border-dashed border-slate-300 dark:border-slate-700 px-4 py-6 text-sm text-slate-500 dark:text-slate-400">
              No claimed domains yet.
            </p>
          ) : (
            <ActiveTable rows={data} />
          )
        ) : null}
      </div>

      {discovered.data && discovered.data.length > 0 ? (
        <DiscoveredTray rows={discovered.data} />
      ) : null}
    </div>
  );
}

function ActiveTable({ rows }: { rows: NonNullable<ReturnType<typeof useDomains>["data"]> }) {
  const del = useDeleteDomain();
  const [confirm, setConfirm] = useState<string | null>(null);

  return (
    <div className="overflow-hidden rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
      <table className="min-w-full text-sm">
        <thead className="bg-slate-50 dark:bg-slate-800/60 text-left text-slate-600 dark:text-slate-400">
          <tr>
            <th className="px-4 py-2 font-medium">Domain</th>
            <th className="px-4 py-2 font-medium">Reports</th>
            <th className="px-4 py-2 font-medium">Last report</th>
            <th className="px-4 py-2 font-medium">Status</th>
            <th className="px-4 py-2 font-medium text-right">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
          {rows.map((d) => (
            <tr key={d.domain}>
              <td className="px-4 py-2 font-mono">
                <Link
                  to="/domains/$domain"
                  params={{ domain: d.domain }}
                  className="text-slate-900 dark:text-slate-100 hover:underline"
                >
                  {d.domain}
                </Link>
                {d.subdomainCount > 0 ? (
                  <span
                    className="ml-2 rounded bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 text-[10px] font-medium text-slate-600 dark:text-slate-400"
                    title="Subdomains seen sending mail under this organizational domain"
                  >
                    +{d.subdomainCount} subdomain{d.subdomainCount === 1 ? "" : "s"}
                  </span>
                ) : null}
              </td>
              <td className="px-4 py-2">{d.reportCount}</td>
              <td className="px-4 py-2 text-slate-600 dark:text-slate-400">{fromUnix(d.lastReportAt)}</td>
              <td className="px-4 py-2">
                {d.issues.absentFor ? (
                  <span className="rounded bg-amber-50 dark:bg-amber-900/30 px-2 py-0.5 text-xs font-medium text-amber-800 dark:text-amber-300">
                    no reports for {d.issues.absentFor}h
                  </span>
                ) : (
                  <span className="rounded bg-emerald-50 dark:bg-emerald-900/30 px-2 py-0.5 text-xs font-medium text-emerald-800 dark:text-emerald-300">
                    ok
                  </span>
                )}
              </td>
              <td className="px-4 py-2 text-right">
                <div className="inline-flex items-center gap-2">
                  <Link
                    to="/validator"
                    search={{ domain: d.domain }}
                    title={`Validate SPF / DMARC DKIM for ${d.domain}`}
                    className="rounded border border-slate-300 dark:border-slate-700 px-2 py-1 text-xs text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
                  >
                    Validate DNS
                  </Link>
                  {confirm === d.domain ? (
                    <>
                      <button
                        type="button"
                        onClick={() => del.mutate(d.domain, { onSettled: () => setConfirm(null) })}
                        disabled={del.isPending}
                        className="rounded border border-red-300 dark:border-red-800 bg-red-600 px-2 py-1 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-50"
                      >
                        {del.isPending ? "Removing…" : "Confirm remove"}
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirm(null)}
                        className="rounded border border-slate-300 dark:border-slate-700 px-2 py-1 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setConfirm(d.domain)}
                      title="Remove this domain and all its data"
                      className="rounded border border-slate-300 dark:border-slate-700 px-2 py-1 text-xs text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20"
                    >
                      Remove
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DiscoveredTray({ rows }: { rows: NonNullable<ReturnType<typeof useDiscoveredDomains>["data"]> }) {
  return (
    <div className="space-y-3">
      <div>
        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Discovered domains</h2>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          These domains sent reports to your mailbox but aren't claimed yet. Their reports are held (not
          counted). Claim one to prove ownership and backfill its history, or dismiss it if it isn't yours.
        </p>
      </div>
      <div className="overflow-hidden rounded-lg border border-amber-200 dark:border-amber-900/50 bg-amber-50/40 dark:bg-amber-900/10">
        <table className="min-w-full text-sm">
          <thead className="bg-amber-100/50 dark:bg-amber-900/20 text-left text-amber-900 dark:text-amber-200">
            <tr>
              <th className="px-4 py-2 font-medium">Domain</th>
              <th className="px-4 py-2 font-medium">Held reports</th>
              <th className="px-4 py-2 font-medium">Last seen</th>
              <th className="px-4 py-2 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-amber-100 dark:divide-amber-900/30">
            {rows.map((d) => (
              <DiscoveredRow key={d.domain} domain={d.domain} held={d.heldReportCount} lastSeen={d.lastSeenAt} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DiscoveredRow({
  domain,
  held,
  lastSeen,
}: {
  domain: string;
  held: number;
  lastSeen: number | null;
}) {
  const add = useAddDomain();
  const verify = useVerifyDomain();
  const del = useDeleteDomain();
  const [challenge, setChallenge] = useState<AddDomainResponse | null>(null);
  const [confirmDismiss, setConfirmDismiss] = useState(false);

  const startClaim = async () => {
    const resp = await add.mutateAsync(domain);
    setChallenge(resp);
  };

  return (
    <>
      <tr>
        <td className="px-4 py-2 font-mono text-slate-900 dark:text-slate-100">{domain}</td>
        <td className="px-4 py-2 text-slate-700 dark:text-slate-300">{held}</td>
        <td className="px-4 py-2 text-slate-600 dark:text-slate-400">{fromUnix(lastSeen)}</td>
        <td className="px-4 py-2 text-right">
          <div className="inline-flex items-center gap-2">
            <button
              type="button"
              onClick={startClaim}
              disabled={add.isPending}
              className="rounded border border-emerald-300 dark:border-emerald-800 bg-emerald-600 px-2 py-1 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              {add.isPending ? "…" : challenge ? "Show steps" : "Claim"}
            </button>
            {confirmDismiss ? (
              <>
                <button
                  type="button"
                  onClick={() => del.mutate(domain, { onSettled: () => setConfirmDismiss(false) })}
                  disabled={del.isPending}
                  className="rounded border border-red-300 dark:border-red-800 bg-red-600 px-2 py-1 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-50"
                >
                  {del.isPending ? "…" : "Confirm dismiss"}
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmDismiss(false)}
                  className="rounded border border-slate-300 dark:border-slate-700 px-2 py-1 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
                >
                  Cancel
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => setConfirmDismiss(true)}
                className="rounded border border-slate-300 dark:border-slate-700 px-2 py-1 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
              >
                Dismiss
              </button>
            )}
          </div>
        </td>
      </tr>
      {challenge && challenge.challenge ? (
        <tr>
          <td colSpan={4} className="px-4 pb-4">
            <div className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 text-sm space-y-3">
              <p className="text-slate-700 dark:text-slate-300">
                Prove you control <span className="font-mono">{domain}</span>: publish this TXT record, then
                verify.
              </p>
              <RecordBox label="Verification TXT" name={challenge.challenge.name} value={challenge.challenge.value} />
              <RecordBox
                label="DMARC (aggregate reporting)"
                name={challenge.dmarcRecord.name}
                value={challenge.dmarcRecord.value}
              />
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => verify.mutate(domain)}
                  disabled={verify.isPending}
                  className="rounded bg-slate-900 dark:bg-slate-100 px-3 py-1.5 text-xs font-medium text-white dark:text-slate-900 hover:opacity-90 disabled:opacity-50"
                >
                  {verify.isPending ? "Checking…" : "Verify & claim"}
                </button>
                {verify.data && !verify.data.verified ? (
                  <span className="text-xs text-amber-700 dark:text-amber-300">
                    Not found yet{verify.data.error ? ` (${verify.data.error})` : ""}. DNS can take a few
                    minutes.
                  </span>
                ) : null}
              </div>
            </div>
          </td>
        </tr>
      ) : null}
    </>
  );
}

function RecordBox({ label, name, value }: { label: string; name: string; value: string }) {
  return (
    <div className="space-y-1">
      <p className="text-xs font-medium text-slate-500 dark:text-slate-400">{label}</p>
      <div className="rounded border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50 p-2 font-mono text-xs text-slate-800 dark:text-slate-200 break-all">
        <div>
          <span className="text-slate-400">name</span> {name}
        </div>
        <div>
          <span className="text-slate-400">value</span> {value}
        </div>
      </div>
    </div>
  );
}
